import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BookIcon = ({ className = "h-6 w-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

const ChevronRightIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const CURRICULUM = [
  {
    subject: 'Reading & Writing',
    programs: [{ name: 'HMH Into Reading', href: 'https://www.hmhco.com/programs/into-reading' }],
  },
  {
    subject: 'Phonics',
    programs: [{ name: 'Amplify (Skills Strand)', href: 'https://amplify.com/programs/amplify-core-knowledge-language-arts/whats-included-k-2/' }],
  },
  {
    subject: 'Math',
    programs: [{ name: 'Illustrative Math', href: 'https://illustrativemathematics.org/' }],
  },
  {
    subject: 'SEL',
    programs: [{ name: 'Choose Creativity', href: 'https://choosecreativity.org/' }],
  },
  {
    subject: 'Social Studies',
    programs: [
      { name: 'Civics for All', href: 'https://sites.google.com/schools.nyc.gov/social-studies-and-civics/civics-for-all' },
      { name: 'Passport to Social Studies', href: 'https://sites.google.com/schools.nyc.gov/social-studies-and-civics/civics-for-all' },
    ],
  },
  {
    subject: 'Science',
    programs: [{ name: 'Amplify Science', href: 'https://amplify.com/programs/amplify-science/' }],
  },
  {
    subject: 'Clusters',
    programs: [
      { name: 'Art', href: 'https://www.weteachnyc.org/resources/collection/blueprint-for-teaching-and-learning-in-the-arts/' },
      { name: 'PE', href: 'https://www.schools.nyc.gov/learning/subjects/physical-education' },
    ],
  },
];

const OurAcademicsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 font-sans selection:bg-cyan-200 selection:text-blue-900">
      <Navbar />

      <main id="main-content">
        {/* Hero Section */}
        <section className="relative flex min-h-[50vh] items-center justify-center overflow-hidden bg-blue-900">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1920&q=80&auto=format"
              alt="Students learning together"
              className="h-full w-full object-cover opacity-30 mix-blend-overlay"
              loading="eager"
            />
            <div className="absolute inset-0 bg-blue-950/75" />
          </div>

          <div className="relative z-10 mx-auto max-w-7xl px-6 py-24 text-center sm:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-5xl font-bold tracking-tighter text-white sm:text-6xl md:text-7xl">
                Our <span className="text-cyan-400">Academics</span>
              </h1>
              <p className="mt-6 text-xl font-medium leading-relaxed text-blue-50 sm:text-2xl">
                The curricula that guide teaching and learning at River East Elementary
              </p>
            </div>
          </div>
        </section>

        {/* Curriculum Grid */}
        <section className="py-24 sm:py-32 bg-white">
          <div className="mx-auto max-w-6xl px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 text-blue-700 mb-6">
                <BookIcon className="h-8 w-8" />
              </div>
              <h2 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-4">
                Curriculum by Subject
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Learn more about the instructional programs we use in each subject area.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {CURRICULUM.map((entry) => (
                <div
                  key={entry.subject}
                  className="rounded-3xl bg-blue-50 p-8 shadow-xl ring-1 ring-gray-900/5 flex flex-col"
                >
                  <h3 className="text-xl font-bold text-blue-900 mb-4">{entry.subject}</h3>
                  <div className="mt-auto space-y-2">
                    {entry.programs.map((program) => (
                      <a
                        key={program.name}
                        href={program.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-between gap-2 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-gray-800 shadow-sm ring-1 ring-gray-900/5 transition-colors hover:bg-blue-900 hover:text-white"
                      >
                        {program.name}
                        <ChevronRightIcon className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="bg-blue-900 py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Questions About Our Curriculum?
              </h2>
              <p className="mt-4 text-lg leading-8 text-blue-100">
                We're happy to walk you through how these programs support our students.
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <a
                  href="mailto:mpanetta@schools.nyc.gov"
                  className="rounded-full bg-white px-8 py-4 text-base font-semibold text-blue-900 shadow-xl transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  Contact Us
                </a>
                <a
                  href="/"
                  className="text-base font-semibold leading-7 text-white hover:text-cyan-400 transition-colors"
                >
                  Back to Home <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OurAcademicsPage;
